/**
 * Instant results on the search page.
 *
 * Horizon re-renders the results section when a filter or sort changes (assets/facets.js)
 * but nothing re-renders it for the query itself, so the search input still needs a full
 * page load. This element closes that gap with the same mechanism: push the new URL, then
 * ask the Section Rendering API for the results section.
 *
 * It lives in its own file, and reads the query from the URL instead of the filters form,
 * so Horizon updates to facets.js or the shipped `_search-input` block don't collide with it.
 */

import { Component } from '@theme/component';
import { buildSectionSelector, sectionRenderer } from '@theme/section-renderer';
import { debounce, startViewTransition } from '@theme/utilities';

const SEARCH_QUERY = 'q';

/**
 * A settled keystroke renders the entire results section — filters, grid and pagination —
 * which is a much heavier request than the 200ms suggestion fetch in predictive-search.js.
 */
const RENDER_DEBOUNCE = 400;

/**
 * @typedef {object} Refs
 * @property {HTMLElement} status - Live region announcing the result count.
 * @property {HTMLElement} [noResults] - Client-owned copy of the "no results" message.
 * @property {HTMLElement} [noResultsText] - Paragraph inside `noResults`.
 *
 * @extends {Component<Refs>}
 */
class AkInstantSearchComponent extends Component {
  requiredRefs = ['status'];

  /** @type {HTMLInputElement | null} */
  #input = null;

  /**
   * The query string the results section currently reflects. Guards against re-rendering
   * for history entries that only changed the hash.
   */
  #renderedSearch = window.location.search;

  connectedCallback() {
    super.connectedCallback();

    this.#input = this.#findInput();
    if (!this.#input) return;

    this.#input.addEventListener('input', this.#onInput);
    this.#input.form?.addEventListener('submit', this.#onSubmit);
    this.#input.form?.addEventListener('click', this.#onApplyClick);
    window.addEventListener('popstate', this.#onPopState);

    // A term typed in the header, or one landed on from a link, arrives already rendered by
    // the server — including the empty grid a partly typed style name produces.
    this.#broadenEmptySearch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#onInput.cancel();
    this.#input?.removeEventListener('input', this.#onInput);
    this.#input?.form?.removeEventListener('submit', this.#onSubmit);
    this.#input?.form?.removeEventListener('click', this.#onApplyClick);
    window.removeEventListener('popstate', this.#onPopState);
  }

  /**
   * The suggestions dropdown ends in a "view all" button, which in the modal simply submits
   * the form. Submitting is prevented here, so on this page the button applies the query and
   * dismisses the dropdown instead of appearing dead.
   *
   * @param {MouseEvent} event
   */
  #onApplyClick = async (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target?.closest('[data-ak-apply-search]')) return;

    this.#onInput.cancel();
    await this.#updateQuery();

    // The panel is shown while the field holds focus, so releasing focus closes it.
    this.#input?.blur();
  };

  #onInput = debounce(() => {
    this.#updateQuery();
  }, RENDER_DEBOUNCE);

  /**
   * Enter would otherwise reload the page to render results this component has already
   * rendered, so submit renders immediately instead of navigating.
   *
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    if (!this.#sectionId) return;

    event.preventDefault();
    this.#onInput.cancel();
    this.#updateQuery();
  };

  /**
   * Back and forward have to restore the input as well as the grid, because the input holds
   * state the URL alone doesn't put back.
   */
  #onPopState = async () => {
    const input = this.#input;
    if (!input) return;

    const { search } = window.location;
    if (search === this.#renderedSearch) return;

    input.value = this.#typedQuery();
    this.#onInput.cancel();

    await this.#render();
  };

  /**
   * The query as the shopper typed it: a wildcard the theme added to broaden an empty
   * search is not something they should read, hear, or find back in the field.
   *
   * @returns {string}
   */
  #typedQuery() {
    const query = new URLSearchParams(window.location.search).get(SEARCH_QUERY) ?? '';
    return query.replace(/\*$/, '');
  }

  async #updateQuery() {
    const input = this.#input;
    if (!input) return;

    const query = input.value.trim();
    const currentUrl = new URL(window.location.href);

    // Typing a character and deleting it inside the debounce window lands here with nothing
    // to do.
    if (query === (currentUrl.searchParams.get(SEARCH_QUERY) ?? '')) return;

    // Filters, sort and `type` are already in the URL — facets.js put them there — so
    // rewriting only `q` keeps them applied across a query change.
    currentUrl.searchParams.set(SEARCH_QUERY, query);
    currentUrl.searchParams.delete('page');

    history.pushState({}, '', currentUrl.toString());

    await this.#render();
    await this.#broadenEmptySearch();
  }

  /**
   * Retries an empty search as a prefix search.
   *
   * Shopify matches whole words it has indexed, so a distinctive style name finds nothing
   * until its last letter is typed — "itz" returns nothing while "itzy" returns eight
   * products. A trailing wildcard finds it. The retry only runs on a search that came back
   * empty, and the typed term goes back into the URL when even the wildcard finds nothing,
   * so the page never describes a query the shopper did not make.
   */
  async #broadenEmptySearch() {
    const url = new URL(window.location.href);
    const query = url.searchParams.get(SEARCH_QUERY) ?? '';

    if (!query || !/[\p{L}\p{N}]$/u.test(query) || this.#resultsCount() > 0) return;

    url.searchParams.set(SEARCH_QUERY, `${query}*`);
    history.replaceState({}, '', url.toString());

    await this.#render();

    if (this.#resultsCount() > 0) return;

    url.searchParams.set(SEARCH_QUERY, query);
    history.replaceState({}, '', url.toString());
    this.#updateMessaging();
  }

  /**
   * Renders the results section for the current URL. Cache behaviour is left at the
   * renderer's default, matching filter changes, which makes backspacing to an earlier
   * query free.
   */
  async #render() {
    const sectionId = this.#sectionId;
    if (!sectionId) return;

    this.#renderedSearch = window.location.search;

    let html = '';

    await startViewTransition(async () => {
      html = await sectionRenderer.renderSection(sectionId);
    }, ['product-grid']);

    // The renderer aborts an in-flight render when a newer one starts, and resolves the
    // abandoned one with an empty string. Announcing then would report the previous count.
    if (!html) return;

    this.#updateMessaging();
  }

  /**
   * Announces the new result count, and takes over the "no results" copy.
   *
   * The copy shipped in the `_search-input` block is rendered by the search *header*
   * section, which this path never re-renders, so it describes whichever query the server
   * saw. Once the query has changed client-side it is stale by definition.
   */
  #updateMessaging() {
    const { status, noResults, noResultsText } = this.refs;
    const query = this.#typedQuery();
    const count = this.#resultsCount();

    status.textContent = query ? this.#format(this.dataset.statusTemplate, count, query) : '';

    for (const element of document.querySelectorAll('.search-results__no-results')) {
      if (element === noResults) continue;
      element.setAttribute('hidden', '');
    }

    if (!noResults) return;

    const showNoResults = Boolean(query) && count === 0;
    noResults.toggleAttribute('hidden', !showNoResults);

    if (showNoResults && noResultsText) {
      noResultsText.textContent = this.#format(this.dataset.noResultsTemplate, count, query);
    }
  }

  /**
   * Fills the placeholder tokens in a translated string rendered by Liquid.
   *
   * @param {string | undefined} template
   * @param {number} count
   * @param {string} query
   * @returns {string}
   */
  #format(template, count, query) {
    if (!template) return '';
    return template.replaceAll('[count]', String(count)).replaceAll('[query]', query);
  }

  /**
   * Reads the count the results section rendered. The filters block carries it, and is only
   * rendered when there is at least one result, so its absence means zero.
   *
   * @returns {number}
   */
  #resultsCount() {
    const sectionId = this.#sectionId;
    if (!sectionId) return 0;

    const section = document.getElementById(buildSectionSelector(sectionId));
    const counter = section?.querySelector('[data-results-count]');

    if (!(counter instanceof HTMLElement)) return 0;

    return Number(counter.dataset.resultsCount) || 0;
  }

  /**
   * The search input lives in a shipped theme block, so it is found by selector rather than
   * by wrapping it.
   *
   * @returns {HTMLInputElement | null}
   */
  #findInput() {
    const scopes = [this.closest('.shopify-section'), document];

    for (const scope of scopes) {
      const input = scope?.querySelector(`input[type="search"][name="${SEARCH_QUERY}"]`);
      if (input instanceof HTMLInputElement) return input;
    }

    return null;
  }

  /**
   * The results section owns its own id, which differs between templates.
   *
   * @returns {string | null}
   */
  get #sectionId() {
    const target = document.querySelector('results-list[section-id], facets-form-component[section-id]');
    return target?.getAttribute('section-id') ?? null;
  }
}

if (!customElements.get('ak-instant-search-component')) {
  customElements.define('ak-instant-search-component', AkInstantSearchComponent);
}
